import React, { useState } from 'react';
import { ThemeContext } from '../../lib/ThemeContext.js';
import { FORM_ROOT_CLASS, formRootAttributes } from '~/lib/FormCssApi';
import { isEmbedded } from '../../public/lib/PageHelpers.js';

// Source: https://github.com/PimpTrizkit/PJs/wiki/12.-Shade,-Blend-and-Convert-a-Web-Color-(pSBC.js)
// Version 4.1
const pSBC = (p, c0, c1, l) => {
    let r,
        g,
        b,
        P,
        f,
        t,
        h,
        m = Math.round,
        a = typeof c1 == 'string';
    if (
        typeof p != 'number' ||
        p < -1 ||
        p > 1 ||
        typeof c0 != 'string' ||
        (c0[0] != 'r' && c0[0] != '#') ||
        (c1 && !a)
    )
        return null;
    (h = c0.length > 9),
        (h = a ? (c1.length > 9 ? true : c1 == 'c' ? !h : false) : h),
        (f = pSBC.pSBCr(c0)),
        (P = p < 0),
        (t =
            c1 && c1 != 'c'
                ? pSBC.pSBCr(c1)
                : P
                  ? { r: 0, g: 0, b: 0, a: -1 }
                  : { r: 255, g: 255, b: 255, a: -1 }),
        (p = P ? p * -1 : p),
        (P = 1 - p);
    if (!f || !t) return null;
    if (l)
        (r = m(P * f.r + p * t.r)),
            (g = m(P * f.g + p * t.g)),
            (b = m(P * f.b + p * t.b));
    else
        (r = m((P * f.r ** 2 + p * t.r ** 2) ** 0.5)),
            (g = m((P * f.g ** 2 + p * t.g ** 2) ** 0.5)),
            (b = m((P * f.b ** 2 + p * t.b ** 2) ** 0.5));
    (a = f.a),
        (t = t.a),
        (f = a >= 0 || t >= 0),
        (a = f ? (a < 0 ? t : t < 0 ? a : a * P + t * p) : 0);
    if (h)
        return (
            'rgb' +
            (f ? 'a(' : '(') +
            r +
            ',' +
            g +
            ',' +
            b +
            (f ? ',' + m(a * 1000) / 1000 : '') +
            ')'
        );
    else
        return (
            '#' +
            (
                4294967296 +
                r * 16777216 +
                g * 65536 +
                b * 256 +
                (f ? m(a * 255) : 0)
            )
                .toString(16)
                .slice(1, f ? undefined : -2)
        );
};

pSBC.pSBCr = d => {
    const i = parseInt;
    let n = d.length,
        x = {};
    if (n > 9) {
        const [r, g, b, a] = (d = d.split(','));
        n = d.length;
        if (n < 3 || n > 4) return null;
        (x.r = i(r[3] == 'a' ? r.slice(5) : r.slice(4))),
            (x.g = i(g)),
            (x.b = i(b)),
            (x.a = a ? parseFloat(a) : -1);
    } else {
        if (n == 8 || n == 6 || n < 4) return null;
        if (n < 6)
            d =
                '#' +
                d[1] +
                d[1] +
                d[2] +
                d[2] +
                d[3] +
                d[3] +
                (n > 4 ? d[4] + d[4] : '');
        d = i(d.slice(1), 16);
        if (n == 9 || n == 5)
            (x.r = (d >> 24) & 255),
                (x.g = (d >> 16) & 255),
                (x.b = (d >> 8) & 255),
                (x.a = Math.round((d & 255) / 0.255) / 1000);
        else
            (x.r = d >> 16),
                (x.g = (d >> 8) & 255),
                (x.b = d & 255),
                (x.a = -1);
    }
    return x;
};

function getTheme(value, options) {
    if (value && options[value]) {
        return options[value];
    } else {
        return options[Object.keys(options)[0]];
    }
}

const ThemedForm = ({
    styles,
    onlyColor,
    objectType,
    objectId,
    state,
    step,
    customCss,
    scopeRoot = true,
    children,
}) => {
    const [scope, setScope] = useState(
        `theme-${parseInt(Math.random() * 100000000)}`,
    );

    const inputTheme = getTheme(styles?.input, {
        'rounded-rectangle': 'theme-input-rounded-rectangle',
        square: 'theme-input-square',
        pill: 'theme-input-pill',
    });

    const buttonTheme = getTheme(styles?.button, {
        'rounded-rectangle': 'theme-button-rounded-rectangle',
        'inverse-rounded-rectangle': 'theme-button-inverse-rounded-rectangle',
        square: 'theme-button-square',
        'inverse-square': 'theme-button-inverse-square',
        pill: 'theme-button-pill',
        'inverse-pill': 'theme-button-inverse-pill',
    });

    const fontTheme = getTheme(styles?.font, {
        sans: 'theme-font-sans',
        serif: 'theme-font-serif',
        mono: 'theme-font-mono',
    });

    const fontSize = getTheme(styles?.fontSize, {
        normal: '16px',
        small: '14px',
        large: '18px',
        'extra-large': '20px',
    });
    const fontSizeSm = getTheme(styles?.fontSize, {
        normal: '12px',
        small: '10px',
        large: '14px',
        'extra-large': '16px',
    });

    const color = styles?.color || '#2C87F3';
    const lightColor = pSBC(0.2, color) || color;
    const lightestColor = pSBC(0.7, color) || color;
    const css = `
.${scope} {
    /* The theme, as custom properties, so custom CSS can restyle a form by
        overriding these rather than by outgunning our selectors */
    --breely-color: ${color};
    --breely-color-light: ${lightColor};
    --breely-color-lightest: ${lightestColor};
    --breely-font-size: ${fontSize};
    --breely-font-size-sm: ${fontSizeSm};

    font-size: var(--breely-font-size);
    .text-base, .choose-block p { font-size: var(--breely-font-size); }
    .input-label, .text-sm, .btn-light-pill-sm { font-size: var(--breely-font-size-sm); }

    &.theme-button-rounded-rectangle .form-button,
    &.theme-button-square .form-button,
    &.theme-button-pill .form-button,
    .themed-button.form-button
    { background-color: var(--breely-color); &:hover {background-color: var(--breely-color-light)} }

    &.theme-button-rounded-rectangle .form-button-light,
    &.theme-button-square .form-button-light,
    &.theme-button-pill .form-button-light,
    .themed-button.form-button-light
    { background-color: var(--breely-color-lightest); color: var(--breely-color) }

    &.theme-button-inverse-rounded-rectangle .form-button,
    &.theme-button-inverse-square .form-button,
    &.theme-button-inverse-pill .form-button,
    .themed-inverse-button.form-button
    { border-color: var(--breely-color); color: var(--breely-color); &:hover {background-color: var(--breely-color); color: #ffffff} }

    &.theme-button-inverse-rounded-rectangle .form-button-light,
    &.theme-button-inverse-square .form-button-light,
    &.theme-button-inverse-pill .form-button-light,
    .themed-inverse-button.form-button-light
    { border-color: var(--breely-color-light); color: var(--breely-color-light); &:hover {background-color: var(--breely-color-lightest); color: var(--breely-color-light)} }


    input:focus, select:focus, textarea:focus { border-color: var(--breely-color) }
    [type='checkbox'], [type='radio'] { color: var(--breely-color) }

    a, .link { color: var(--breely-color) }
    .link-no-underline { color: var(--breely-color) }

    section.choose-block:hover, section.choose-block.selected-block { border-color: var(--breely-color); outline-color: var(--breely-color); color: var(--breely-color); }
    section.choose-block:hover div:last-of-type,
    section.choose-block.selected-block div:last-of-type,
    section.choose-block.choose-block-selected div:last-of-type
        { color: var(--breely-color) }

    .availability-picker button { background-color: transparent; color: inherit }
    .time-option:hover { border-color: var(--breely-color); color: var(--breely-color) }
    button.time-option-selected { border-color: var(--breely-color); color: var(--breely-color) }
    .date-option-selected { color: var(--breely-color) !important; background-color: var(--breely-color-lightest) !important; }

    .availability-picker .react-calendar__navigation__prev-button,
    .availability-picker .react-calendar__navigation__next-button {
        color: var(--breely-color)
    }

    .availability-picker button.react-calendar__tile--active,
    .availability-picker .react-calendar__tile--active:enabled:hover,
    .availability-picker .react-calendar__tile--active:enabled:focus {
        color: var(--breely-color);
        background-color: white;
        box-shadow: 0px 0px 0px 2px var(--breely-color);
    }

    .event-confirmation-icon { color: var(--breely-color) }

    .btn-inverse, .form-button.btn-inverse { background-color: var(--breely-color-lightest); color: var(--breely-color) }
    .btn-inverse:hover, .form-button.btn-inverse:hover { background-color: var(--breely-color-lightest) }
    .btn-danger, .form-button.btn-danger { background-color: #fee2e2; color: #D25D5D }
    .btn-danger:hover, .form-button.btn-danger:hover { background-color: #fee2e2 }

    .themed-text { color: var(--breely-color) }
    .themed-outline { outline-color: var(--breely-color) }
    .themed-border { border-color: var(--breely-color) }
    .hover-themed-border:hover { border-color: var(--breely-color) }
    .themed-bg-light { background-color: var(--breely-color-lightest) }
    .themed-bg { background-color: var(--breely-color) }

    .themed-slider { --theme-color: var(--breely-color); -webkit-appearance: none; appearance: none; background: transparent; }
    .themed-slider::-webkit-slider-runnable-track {
        height: 8px;
        border-radius: 4px;
        background: linear-gradient(to right, var(--breely-color) var(--slider-progress, 50%), var(--breely-color-lightest) var(--slider-progress, 50%));
    }
    .themed-slider::-moz-range-track {
        height: 8px;
        border-radius: 4px;
        background: var(--breely-color-lightest);
        border: none;
    }
    .themed-slider::-moz-range-progress {
        height: 8px;
        border-radius: 4px;
        background: var(--breely-color);
    }
    .themed-slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: white;
        cursor: pointer;
        border: 2px solid var(--breely-color);
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        margin-top: -6px;
    }
    .themed-slider::-moz-range-thumb {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: white;
        cursor: pointer;
        border: 2px solid var(--breely-color);
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }

    .thank-you-check-container { background-color: var(--breely-color-lightest); color: var(--breely-color) }
}
    `;

    // onlyColor is a swatch inside the admin styles editor rather than a form.
    //  scopeRoot is turned off where something further up already owns the
    //  root, ex. the admin designer, which themes each question separately but
    //  wants custom CSS scoped to the preview as a whole
    const isScopeRoot = !onlyColor && scopeRoot;
    const className = onlyColor
        ? `themed ${scope}`
        : `themed ${isScopeRoot ? FORM_ROOT_CLASS : ''} ${scope} ${fontTheme} ${inputTheme} ${buttonTheme}`;

    return (
        <ThemeContext.Provider
            value={{
                color: color,
                styles: styles,
            }}
        >
            <div
                className={className}
                {...(!isScopeRoot
                    ? {}
                    : formRootAttributes({
                          objectType,
                          objectId,
                          state,
                          step,
                          isEmbedded: isEmbedded(),
                      }))}
            >
                <style type="text/css">{css}</style>
                {/* Sanitized server side, and last so it can override the
                    theme above without having to outweigh its selectors */}
                {!!customCss && <style type="text/css">{customCss}</style>}
                {children}
            </div>
        </ThemeContext.Provider>
    );
};

export default ThemedForm;
